// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import Queue from '@segment/localstorage-retry?client';

/**
 * Queue options
 *
 * Attempt with exponential backoff for upto 10 times.
 * Backoff periods are: 1s, 2s, 4s, 8s, 16s, 32s, 64s, 128s (~2m), 256s (~4m),
 * 512s (~8.5m) and 1024s (~17m).
 */
export const queueOptions = {
    maxRetryDelay: 360000, // max interval of 1hr. Added as a guard.
    minRetryDelay: 1000, // first attempt (1s)
    backoffFactor: 2,
    maxAttempts: 10,
    maxItems: 100,
};

export const SEND_TIMEOUT_MS = 15000;

export interface QueueItem {
    url: string;
    useFallbackHost?: boolean;
    message: {
        messageId?: string;
        sentAt?: string;
        [key: string]: unknown;
    };
}

export type QueueLibrary = new (
    name: string,
    options: typeof queueOptions,
    processor: typeof queueExecute,
) => Queue;

// The ?client import is stripped from server-side bundles, which leaves the
// identifier undeclared there; typeof is the only safe way to probe it.
const getDefaultQueueLibrary = (): QueueLibrary | undefined =>
    typeof Queue === 'undefined' ? undefined : Queue;

let queue: Queue = null;

export const getQueue = (QueueImpl = getDefaultQueueLibrary()) => {
    if (!queue) {
        queue = initializeQueue(QueueImpl);
    }

    return queue;
};

const getFallbackHost = (): string | undefined => import.meta.env.VITE_TRACKING_API_FALLBACK;

export const resolveQueuedUrl = (item: QueueItem) => {
    const fallbackHost = getFallbackHost();

    if (item.useFallbackHost && fallbackHost) {
        return `${fallbackHost}${item.url}`;
    }

    return item.url;
};

export const logProcessedError = (
    err: { status: string; responseText: string } | null,
    res: unknown,
    item: QueueItem,
) => {
    if (err) {
        const lastPart = item.url.split('/').reverse()[0];

        console.warn(`Tracking Error: /${lastPart} - ${err.status}`, {
            data: {
                ...item,
                error: JSON.stringify(err),
                status: err.status,
                responseText: err.responseText,
            },
        });
    }
};

export const initializeQueue = (QueueImpl = getDefaultQueueLibrary()) => {
    if (!QueueImpl) {
        throw new Error('The tracking queue is only available in the browser bundle');
    }

    const queue = new QueueImpl('perspective', queueOptions, queueExecute);

    queue.on('processed', logProcessedError);

    queue.shouldRetry = function (item: QueueItem, attemptNumber: number) {
        return shouldRetryQueuedItem(
            item,
            attemptNumber,
            this._store.get(this.keys.QUEUE) || [],
            this.maxAttempts,
        );
    };

    queue.start();

    return queue;
};

export const shouldRetryQueuedItem = (
    item: QueueItem,
    attemptNumber: number,
    storedQueue: Array<{ item: QueueItem }>,
    maxAttempts: number,
) => {
    const messageId = item.message.messageId;
    const alreadyQueued =
        messageId !== undefined &&
        storedQueue.some((queueContent) => queueContent.item.message.messageId === messageId);

    if (alreadyQueued) {
        return false;
    }

    if (attemptNumber > 0) {
        item.useFallbackHost = true;
    }

    return attemptNumber <= maxAttempts;
};

export const queueExecute = (
    item: QueueItem,
    done: (req: XMLHttpRequest | null, ev?: ProgressEvent<EventTarget>) => void,
) => {
    // apply sentAt at flush time and reset on each retry
    // so the tracking-api doesn't interpret a time skew
    item.message.sentAt = new Date().toISOString();

    send(item.message, resolveQueuedUrl(item), (req: XMLHttpRequest, res: ProgressEvent) => {
        if (req) {
            return done(req);
        }
        return done(null, res);
    });
};

export const send = (body: any, url: string, onFail: any, timeout = SEND_TIMEOUT_MS) => {
    // use XMLHttpRequest because of browser support and backwards compatibility
    // https://caniuse.com/?search=XMLHttpRequest
    const req = new XMLHttpRequest();

    const done = () => {
        // https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/readyState
        // when operation is complete
        if (req.readyState === 4) {
            if (req.status !== 200) {
                onFail(req);
            } else {
                onFail(null, req);
            }
        }
    };

    req.onerror = onFail;
    req.onreadystatechange = done;
    req.open('POST', url, true);

    req.timeout = timeout;
    req.ontimeout = onFail;

    req.setRequestHeader('Content-Type', 'text/plain');
    req.send(JSON.stringify(body));
};
